import { parseDecimalBR } from "./numberFormat";
import { taxaOverrideError } from "./operations";

/**
 * Construção do bloco `fees` dos payloads de antecipação do estoque
 * (POST /v2/stock/simulate-anticipation e /v2/stock/request-anticipation).
 *
 * Decisão do owner (2026-07-16, goal quatro-frentes round γ): o Portal
 * OMITE fees para cedente governado — as chaves (monthly_rate_pct,
 * floating_days) saem do payload POR COMPLETO (nunca null/0) e a V2
 * resolve pela policy do principal. Contexto: o corretivo da V2 passa a
 * responder 422 (standard_rate_required) a QUALQUER override de pricing
 * no canal direto; sob parceiro, a taxa do payload só é aceita dentro da
 * banda aprovada na policy.
 *
 * Regras espelhadas aqui:
 *  - perfil cedente (portal_type === "cedente"): NUNCA envia fees;
 *  - perfil parceiro: fees só com OPT-IN explícito (campo taxa preenchido
 *    e > 0) — em branco, vale a taxa da policy configurada na V2 (mesma
 *    convenção do wizard de operação, NewOperation.jsx). Os antigos
 *    defaults hardcoded taxa=3.5/floating=2 foram removidos: eram enviados
 *    em toda simulação/solicitação e quebrariam (422) o fluxo de estoque
 *    de cedente governado na janela do deploy da V2.
 *  - floating_days é override de taxa por outra via — só acompanha uma taxa
 *    explícita, nunca viaja sozinho. Semântica pós-β da V2 (2026-07-16):
 *    floating SOMA dias ao prazo cobrado do deságio (25 dias + 2 de floating
 *    ⇒ cobra 27) — antes subtraía ("carência"); os rótulos do front seguem
 *    a semântica nova.
 *
 * @param {Object} opts
 * @param {boolean} opts.isCedente - usuário do portal do cedente (governado)
 * @param {string|number} opts.taxa - taxa mensal (%) digitada, MASCARADA em
 *   pt-BR ("3,5") ou canônica ("3.5"); ""/0 = sem override
 * @param {string|number} opts.floating - dias de floating digitados; ""/0 = sem override.
 *   Só INTEIRO positivo viaja: `parseInt` aceitaria "1e5" como 1 e "12abc" como
 *   12 — a mesma classe de "texto inválido virando número" que as máscaras
 *   fecham na entrada.
 * @returns {{monthly_rate_pct: number, floating_days?: number}|null}
 *   objeto fees a anexar ao body, ou null para OMITIR a chave `fees`.
 */
export function buildStockFees({ isCedente, taxa, floating }) {
  if (isCedente) return null;
  // `parseDecimalBR` e não `parseFloat`: a taxa chega mascarada do input
  // ("3,5") e `parseFloat("3,5")` seria 3 — override de PRICING errado.
  // Vazio→null e inválido→NaN reprovam em `> 0` como o NaN do parseFloat.
  const rate = parseDecimalBR(taxa);
  if (!(rate > 0)) return null;
  const fees = { monthly_rate_pct: rate };
  // `parseDecimalBR` e não `parseInt`: o parse é ESTRITO (texto inválido → NaN),
  // enquanto `parseInt("1e5")` seria 1 — dia de floating fabricado a partir de
  // lixo. `Number.isInteger` fecha a porta para "2,5 dias".
  const days = parseDecimalBR(floating);
  if (Number.isInteger(days) && days > 0) fees.floating_days = days;
  return fees;
}

/**
 * Rótulo da taxa exibido na confirmação: com override explícito mostra a
 * taxa digitada; sem override (ou perfil cedente) deixa claro que vale a
 * policy da V2 — antes exibia o default hardcoded "3.5% a.m. + 2d".
 */
export function stockFeeLabel({ isCedente, taxa, floating }) {
  const fees = buildStockFees({ isCedente, taxa, floating });
  if (!fees) return "Policy V2 (taxa padrão)";
  const days = fees.floating_days;
  // Pós-β: floating SOMA ao prazo cobrado — o rótulo "carencia" ficou invertido.
  return `${fees.monthly_rate_pct}% a.m.${days ? ` + ${days}d floating` : ""}`;
}

/**
 * Bloco `bank` do payload de antecipação do estoque.
 *
 * PIX no CNPJ do cedente é o ÚNICO meio de pagamento implementado. O modo
 * "conta bancária" da tela foi removido (decisão do owner 2026-08-03): era UI
 * sem estado que mandava banco/agência/conta VAZIOS. Está aqui, e não inline no
 * Stock.jsx, para que o contrato seja pinado por EXECUÇÃO (inspecionando o
 * objeto) e não por busca textual na fonte.
 *
 * @returns {{use_document_pix: boolean}} objeto `bank` do body
 */
export function buildAnticipationBank() {
  return { use_document_pix: true };
}

/**
 * Campo de override PREENCHIDO mas inválido ⇒ mensagem de bloqueio.
 *
 * `buildStockFees` OMITE `fees` quando o valor não parseia — e omitir é
 * indistinguível de "campo em branco", então a simulação/solicitação seguia
 * pela policy da V2 em SILÊNCIO enquanto o usuário achava que tinha travado a
 * taxa. Vazio continua significando "use a policy"; preenchido-e-inválido
 * passa a BLOQUEAR a ação, nomeando o campo.
 *
 * Zero segue sendo "sem override" (contrato antigo, não é erro).
 *
 * @returns {string|null} mensagem para o toast, ou null quando pode seguir
 */
export function stockFeesError({ isCedente, taxa, floating }) {
  if (isCedente) return null; // o cedente nem vê os campos: fees nunca viaja
  const taxaErro = taxaOverrideError(taxa); // MESMA regra e mensagem do wizard
  if (taxaErro) return taxaErro;
  if (String(floating ?? "").trim() !== "") {
    const dias = parseDecimalBR(floating);
    if (!Number.isInteger(dias) || dias < 0) {
      return "Dias de floating inválido — use um número inteiro de dias ou deixe o campo em branco para usar a policy da V2.";
    }
  }
  return null;
}
